import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventDropLogo, EventDropNav } from '../components/EventDropLogo';
import SuppliersSection from '../components/SuppliersSection';
import { QrCode, VideoCamera, FilmStrip, Camera, Microphone, Lightning, Envelope, Play, Check, CaretDown, Star, ArrowRight } from '@phosphor-icons/react';
import { motion, useScroll, useTransform } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingV3 = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [showSticky, setShowSticky] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const handleScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.12 } }
  };

  return (
    <div className="min-h-screen bg-[#15161B] text-white overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* Scroll progress bar */}
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-0 left-0 right-0 h-[3px] z-[60] origin-left">
        <div className="w-full h-full" style={{ background: 'linear-gradient(90deg, #B89762, #C29D52)' }} />
      </motion.div>

      {/* Ambient floating glow orbs */}
      <motion.div className="fixed top-20 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none z-0" style={{ background: 'radial-gradient(circle, rgba(108,92,231,0.1), transparent 70%)' }}
        animate={{ x: [0, 80, 0], y: [0, 40, 0] }} transition={{ repeat: Infinity, duration: 12, ease: 'easeInOut' }} />
      <motion.div className="fixed bottom-20 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none z-0" style={{ background: 'radial-gradient(circle, rgba(62,198,231,0.08), transparent 70%)' }}
        animate={{ x: [0, -60, 0], y: [0, -30, 0] }} transition={{ repeat: Infinity, duration: 15, ease: 'easeInOut' }} />

      {/* Falling confetti particles */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {Array.from({ length: 20 }).map((_, i) => (
          <div key={`confetti-${i}`} className="absolute" style={{
            left: `${(i * 5.3 + 7) % 100}%`,
            top: `${(i * 4.7 + 3) % 100}%`,
            width: `${5 + (i % 4) * 2}px`,
            height: `${5 + (i % 4) * 2}px`,
            borderRadius: i % 2 === 0 ? '50%' : '2px',
            background: ['#FF0099', '#00FFDD', '#FFCC00', '#B89762', '#C29D52', '#FF6B6B'][i % 6],
            opacity: 0.12 + (i % 5) * 0.03,
            animation: `confettiFall ${8 + (i % 7) * 2}s linear infinite`,
            animationDelay: `${(i * 0.7) % 10}s`,
          }} />
        ))}
      </div>
      <style>{`@keyframes confettiFall { 0% { transform: translateY(-10vh) rotate(0deg); } 100% { transform: translateY(110vh) rotate(720deg); } }`}</style>

      {/* ========== NAV ========== */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 py-3" style={{ background: 'rgba(15,18,34,0.85)', backdropFilter: 'blur(16px)' }}>
        <div className="max-w-[480px] mx-auto flex items-center justify-between">
          <EventDropNav onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-xs font-semibold text-gray-400 hover:text-white transition-colors" data-testid="nav-sign-in">Sign In</button>
            <button onClick={() => navigate('/create')} className="px-4 py-2 rounded-[14px] text-xs font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="nav-cta">Get Started</button>
          </div>
        </div>
      </nav>

      <div className="max-w-[480px] mx-auto px-4">

        {/* ========== 1. HERO ========== */}
        <motion.section ref={heroRef} className="pt-24 pb-12 text-center" initial="hidden" animate="visible" variants={stagger} data-testid="hero-section">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B89762]/10 border border-[#B89762]/50/20 text-[#C29D52] text-[13px] font-semibold mb-6">
            <QrCode size={14} weight="bold" /> Setup in under 60 seconds
          </motion.div>

          <motion.h1 variants={fadeUp} className="text-[34px] font-bold leading-[1.2] tracking-tight mb-4 max-w-[320px] mx-auto" data-testid="hero-title">
            Capture Every Moment of Your Event — <span style={{ background: 'linear-gradient(135deg, #B89762, #7F8AFF, #C29D52)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Even the Ones You Missed</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-base text-gray-400 mb-6 max-w-[320px] mx-auto leading-relaxed">
            Guests upload photos, leave video messages, and everything becomes your private gallery and event film.
          </motion.p>

          <motion.p variants={fadeUp} className="text-[13px] text-gray-500 mb-2">
            No app needed &bull; Works on any phone &bull; Set up in 60 seconds
          </motion.p>

          <motion.p variants={fadeUp} className="text-[13px] text-[#C29D52]/80 font-medium mb-8">
            Perfect for weddings, birthdays &amp; parties
          </motion.p>

          <motion.div variants={fadeUp}>
            <button onClick={() => navigate('/create')} className="w-full h-[52px] rounded-[14px] text-base font-bold text-white transition-all active:scale-[0.98]" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="hero-cta">
              Create Your Event
            </button>
            <p className="text-[13px] text-gray-500 mt-3">From £49</p>
            <p className="text-[11px] text-[#C29D52]/50 mt-1">Limited-time launch pricing</p>
          </motion.div>
        </motion.section>

        {/* ========== 2. VIDEO PROOF ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold text-center mb-6">See It In Action</motion.h2>
          <motion.div variants={fadeUp} className="rounded-2xl overflow-hidden border border-white/10 relative bg-black" style={{ aspectRatio: '16/9' }}>
            <video
              src={`${BACKEND_URL}/api/demo-video`}
              poster="/demo-poster.jpg"
              className="w-full h-full object-cover"
              playsInline muted autoPlay loop controls
              data-testid="demo-video"
            />
          </motion.div>
          <p className="text-[12px] text-gray-500 text-center mt-3">A real event — Ava's 18th · 140 moments captured</p>
        </motion.section>

        {/* ========== 3. VALUE STACK ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold text-center mb-8">More Than a Photobooth</motion.h2>
          <div className="space-y-6">
            {[
              { icon: <Camera size={28} weight="bold" />, title: 'Pocket Booth', desc: 'Multi-device sync capture, QR join, 7 templates & boomerangs', color: '#FF0099' },
              { icon: <QrCode size={28} weight="bold" />, title: 'All Guest Photos', desc: 'Every moment, from every angle', color: '#C29D52' },
              { icon: <Microphone size={28} weight="bold" />, title: 'Video Messages', desc: 'Hear what people really felt', color: '#B89762' },
              { icon: <FilmStrip size={28} weight="bold" />, title: 'Event Film', desc: 'Your whole event, beautifully stitched together', color: '#A033D8' },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}20`, color: item.color }}>
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-bold text-base mb-0.5">{item.title}</h3>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ========== 4. FILM SECTION ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold text-center mb-4">Your Event, Turned Into a Film</motion.h2>
          <motion.p variants={fadeUp} className="text-base text-gray-400 text-center leading-relaxed mb-2">
            Relive your entire event through a beautifully edited highlight — created automatically from your guests' photos and videos.
          </motion.p>
          <motion.p variants={fadeUp} className="text-sm text-[#C29D52]/70 text-center italic mb-6">
            See moments you didn't even know happened.
          </motion.p>
          <motion.div variants={fadeUp} className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#B89762]/10 to-[#15161B] p-8 text-center">
            <FilmStrip size={56} weight="bold" className="mx-auto mb-4 text-[#B89762]" />
            <p className="text-sm text-gray-400">AI-powered editing &bull; Music &bull; Transitions</p>
          </motion.div>
        </motion.section>

        {/* ========== 4b. POCKET BOOTH PROMO ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.div variants={fadeUp} className="rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #1a0033 0%, #0d1b2a 50%, #1a0033 100%)', border: '1.5px solid rgba(255,0,153,0.3)' }}>
            <div className="absolute top-0 right-0 px-4 py-1.5 rounded-bl-xl text-[11px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #FF0099, #B89762)' }}>
              NEW
            </div>
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF0099, #B89762)' }}>
                <Camera size={28} weight="bold" className="text-white" />
              </div>
              <h3 className="text-xl font-black mb-1">Pocket Booth</h3>
              <p className="text-sm text-gray-400 mb-4">Turn every phone into a photo booth</p>

              <div className="grid grid-cols-2 gap-2 mb-5 text-left max-w-xs mx-auto">
                {[
                  'Multi-device sync capture',
                  'QR code instant join',
                  '7 design templates',
                  'Boomerang loops',
                  'Live gallery + likes',
                  'Auto event film',
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[12px] text-gray-300">
                    <Check size={12} weight="bold" className="text-[#B89762] flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-2xl font-black text-white">£49</span>
                <span className="text-sm text-gray-500 line-through">£79</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ background: '#FF0099' }}>SAVE £30</span>
              </div>

              <button onClick={() => navigate('/checkout?products=pocket_booth')}
                className="w-full h-12 rounded-xl text-sm font-bold text-white transition-all active:scale-[0.97]"
                style={{ background: 'linear-gradient(135deg, #FF0099, #B89762)' }}
                data-testid="booth-promo-cta">
                Add Pocket Booth
              </button>
            </div>
          </motion.div>
        </motion.section>

        {/* ========== 5. PRICING ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger} data-testid="pricing-section">
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold text-center mb-2">Choose Your Experience</motion.h2>
          <motion.p variants={fadeUp} className="text-[13px] text-gray-500 text-center mb-3">One simple payment. No subscriptions.</motion.p>
          <motion.div variants={fadeUp} className="flex justify-center gap-1 mb-8">
            {[...Array(5)].map((_, i) => <Star key={`price-star-${i}`} size={18} weight="fill" className="text-amber-400" />)}
          </motion.div>

          <div className="space-y-4">
            {/* THE SNAP — Teal/Cyan theme */}
            <motion.div variants={fadeUp} className="rounded-2xl p-5 relative" style={{ background: '#1A1E3A', border: '1.5px solid rgba(0,240,255,0.35)', boxShadow: '0 0 20px rgba(0,240,255,0.08), inset 0 1px 0 rgba(0,240,255,0.1)' }} data-testid="pricing-snap">
              <h3 className="text-lg font-bold mb-1" style={{ color: '#70FF9A' }}>THE SNAP</h3>
              <p className="text-3xl font-black mb-2">£49</p>
              <p className="text-sm text-gray-400 mb-4">Simple. Capture everything.</p>
              <div className="space-y-2 mb-5">
                {['Unlimited uploads', 'Private gallery', 'QR access'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm"><Check size={14} weight="bold" style={{ color: '#00F0FF' }} className="flex-shrink-0" />{f}</div>
                ))}
              </div>
              <button onClick={() => navigate('/checkout?package=snap')} className="w-full h-[52px] rounded-[14px] text-sm font-bold text-white transition-all active:scale-[0.98]" style={{ background: 'linear-gradient(135deg, #B89762, #00A38A)' }} data-testid="cta-snap">Start with Snap</button>
            </motion.div>

            {/* THE STORY (FEATURED) — Purple/Blue theme */}
            <motion.div variants={fadeUp} className="rounded-2xl p-5 relative" style={{ background: 'linear-gradient(180deg, #2A2E5B, #1E2248)', border: '2px solid rgba(127,58,255,0.6)', boxShadow: '0 0 30px rgba(127,58,255,0.15), inset 0 1px 0 rgba(127,58,255,0.15)', transform: 'scale(1.02)' }} data-testid="pricing-story">
              <div className="absolute -top-3 left-5 px-3 py-1 rounded-full text-[11px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #7F3AFF, #3A4FFF)' }}>
                <Star size={10} weight="fill" className="inline mr-1" />MOST POPULAR
              </div>
              <h3 className="text-lg font-bold mb-1 mt-2" style={{ color: '#A970FF' }}>THE STORY</h3>
              <p className="text-3xl font-black mb-2">£99</p>
              <p className="text-sm text-gray-400 mb-1">Moments + voices + memories</p>
              <p className="text-[11px] font-medium mb-4" style={{ color: 'rgba(169,112,255,0.7)' }}>Most couples choose this</p>
              <div className="space-y-2 mb-5">
                {['Everything in Snap', 'Video guestbook', 'Highlight Reel'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm"><Check size={14} weight="bold" style={{ color: '#7F3AFF' }} className="flex-shrink-0" />{f}</div>
                ))}
              </div>
              <button onClick={() => navigate('/checkout?package=story')} className="w-full h-[52px] rounded-[14px] text-sm font-bold text-white transition-all active:scale-[0.98]" style={{ background: 'linear-gradient(135deg, #7F3AFF, #3A4FFF)', boxShadow: '0 4px 20px rgba(127,58,255,0.3)' }} data-testid="cta-story">Get The Story</button>
            </motion.div>

            {/* THE LEGACY — Magenta/Pink theme */}
            <motion.div variants={fadeUp} className="rounded-2xl p-5 relative" style={{ background: 'linear-gradient(180deg, #3D2255, #2A1840)', border: '1.5px solid rgba(224,0,255,0.35)', boxShadow: '0 0 20px rgba(224,0,255,0.08), inset 0 1px 0 rgba(224,0,255,0.1)' }} data-testid="pricing-legacy">
              <div className="absolute -top-3 left-5 px-3 py-1 rounded-full text-[11px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #E000FF, #FF00A3)' }}>BEST VALUE</div>
              <h3 className="text-lg font-bold mb-1 mt-2" style={{ color: '#FF55CC' }}>THE LEGACY</h3>
              <p className="text-3xl font-black mb-2">£159</p>
              <p className="text-sm text-gray-400 mb-4">Capture everything. Relive it forever.</p>
              <div className="space-y-2 mb-5">
                {['Everything in Story', 'Photobooth mode', 'Pocket Booth', 'Event Film', 'Challenge Mode'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm"><Check size={14} weight="bold" style={{ color: '#E000FF' }} className="flex-shrink-0" />{f}</div>
                ))}
              </div>
              <button onClick={() => navigate('/checkout?package=legacy')} className="w-full h-[52px] rounded-[14px] text-sm font-bold text-white transition-all active:scale-[0.98]" style={{ background: 'linear-gradient(135deg, #B030D0, #8B20A0)', boxShadow: '0 4px 15px rgba(224,0,255,0.2)' }} data-testid="cta-legacy">Go All In</button>
              <p className="text-[11px] font-medium mt-3 text-center" style={{ color: 'rgba(224,0,255,0.5)' }}>Best for weddings &amp; large events</p>
            </motion.div>
          </div>
        </motion.section>

        {/* ========== 6. SOCIAL PROOF ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold text-center mb-2">Loved At Real Events</motion.h2>
          <motion.p variants={fadeUp} className="text-[13px] text-gray-500 text-center mb-6">Trusted by couples across the UK</motion.p>
          <motion.div variants={fadeUp} className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => <Star key={i} size={20} weight="fill" className="text-amber-400" />)}
          </motion.div>
          <div className="space-y-4">
            {[
              '"Guests captured moments we never saw"',
              '"The video made us cry"',
              '"So easy — everyone used it"',
            ].map((q, i) => (
              <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/10 bg-[#15161B] p-5 text-center">
                <p className="text-sm text-gray-300 italic">{q}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ========== 7. HOW IT WORKS ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold text-center mb-2">How It Works</motion.h2>
          <motion.p variants={fadeUp} className="text-[13px] text-gray-500 text-center mb-8">Takes less than 60 seconds to set up</motion.p>
          <div className="space-y-5">
            {[
              { num: '1', icon: <QrCode size={22} weight="bold" />, label: 'Create your event' },
              { num: '2', icon: <ArrowRight size={22} weight="bold" />, label: 'Share your QR code' },
              { num: '3', icon: <Camera size={22} weight="bold" />, label: 'Guests upload instantly' },
              { num: '4', icon: <FilmStrip size={22} weight="bold" />, label: 'Relive everything' },
            ].map((step, i) => (
              <motion.div key={i} variants={fadeUp} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-black" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
                  {step.num}
                </div>
                <p className="font-semibold text-base">{step.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ========== 8. ADD-ONS ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold text-center mb-1">Enhance Your Event</motion.h2>
          <motion.p variants={fadeUp} className="text-[13px] text-gray-500 text-center mb-6">(Optional)</motion.p>
          <motion.div variants={fadeUp} className="rounded-2xl border border-white/10 bg-[#15161B] p-5">
            {[
              { name: 'Photobooth Software', price: '+£79' },
              { name: 'Pocket Booth', price: '+£49' },
              { name: 'Guestbook', price: '+£39' },
              { name: 'Event Film upgrade', price: '+£49' },
              { name: 'Invites', price: '+£29' },
            ].map((addon, i) => (
              <div key={i} className={`flex items-center justify-between py-3 ${i < 3 ? 'border-b border-white/5' : ''}`}>
                <span className="text-sm font-medium">{addon.name}</span>
                <span className="text-sm font-bold text-[#C29D52]">{addon.price}</span>
              </div>
            ))}
          </motion.div>
        </motion.section>

        {/* ========== 9. FAQ ========== */}
        <motion.section className="py-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold text-center mb-6">FAQ</motion.h2>
          <motion.div variants={fadeUp} className="space-y-2">
            {[
              { q: 'Do guests need to download an app?', a: 'No. Guests simply scan a QR code and upload directly from their phone browser. No app, no login, no friction.' },
              { q: 'Is there a limit on uploads?', a: 'No. Unlimited photo and video uploads across all packages. Every moment counts.' },
              { q: 'How fast is setup?', a: 'Under 60 seconds. Create your event, get your QR code, and you\'re ready.' },
              { q: 'Is this a subscription?', a: 'No. One-time payment per event. No recurring charges, no hidden fees.' },
              { q: 'Can I download all photos at once?', a: 'Yes. Download your entire gallery as a ZIP file with one click from your dashboard.' },
            ].map((faq, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-[#15161B] overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`faq-${i}`}>
                  <span className="text-sm font-semibold pr-4">{faq.q}</span>
                  <CaretDown size={16} weight="bold" className={`flex-shrink-0 text-gray-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-sm text-gray-400 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </motion.div>
        </motion.section>

        {/* ========== 10. FINAL CTA ========== */}
        <motion.section className="py-16 text-center" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-2xl font-semibold mb-3">Don't Miss The Moments You Didn't See</motion.h2>
          <motion.p variants={fadeUp} className="text-base text-gray-400 mb-8">Set up your event in under 60 seconds.</motion.p>
          <motion.div variants={fadeUp}>
            <button onClick={() => navigate('/create')} className="w-full h-[52px] rounded-[14px] text-base font-bold text-white transition-all active:scale-[0.98]" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="final-cta">
              Create Your Event
            </button>
            <p className="text-[13px] text-gray-500 mt-3">Takes 60 seconds &bull; No app required</p>
            <p className="text-[13px] text-gray-500 mt-1">From £49</p>
          </motion.div>
        </motion.section>

        {/* Bottom spacer for sticky CTA */}
        <div className="h-20" />

        {/* ========== FOR SUPPLIERS (Studio Pass) ========== */}
        <SuppliersSection />

        {/* ========== FOOTER ========== */}
        <footer className="border-t border-white/5 py-8 text-center">
          <p className="text-[13px]" style={{ color: '#D4AF5E', fontFamily: 'Georgia, "Times New Roman", serif', letterSpacing: '0.02em' }}>EventDrop ✦ &copy; {new Date().getFullYear()}</p>
          <div className="flex justify-center gap-4 mt-2">
            <span className="text-[11px] text-gray-600 hover:text-gray-400 cursor-pointer transition-colors">Privacy Policy</span>
            <span className="text-[11px] text-gray-600 hover:text-gray-400 cursor-pointer transition-colors">Terms of Service</span>
            <span className="text-[11px] text-gray-600 hover:text-gray-400 cursor-pointer transition-colors">Contact</span>
          </div>
        </footer>
      </div>

      {/* ========== STICKY CTA ========== */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ${showSticky ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`} style={{ background: 'rgba(15,18,34,0.95)', backdropFilter: 'blur(16px)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-[480px] mx-auto px-4 flex items-center gap-3 h-16">
          <button onClick={() => navigate('/create')} className="flex-1 h-11 rounded-[14px] text-sm font-bold text-white transition-all active:scale-[0.98]" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="sticky-cta">
            Create Your Event
          </button>
          <span className="text-[13px] text-gray-500 flex-shrink-0">From £49</span>
        </div>
      </div>
    </div>
  );
};

export default LandingV3;
